import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  useFetchMetaDashboardQuery,
  useFetchMetaHierarchyQuery,
  useFetchMetaComparisonQuery,
} from "../../../api/analyticsApi";
import { Btn, Notice, Select, TextInput } from "../../catalogProductStudio/edit/StudioUi";
import { ChartFrame, useChartPalette } from "../AnalyticsCharts";
import { DataState, DataTable, KpiCard, Panel } from "../AnalyticsUi";
import metaCopy from "../metaAdsTranslations";
import { bucketLabel, fill } from "../analyticsModel";
import { META_KPIS, META_PRESETS, metaFormat, readMetaFilters, validMetaDates } from "../metaAdsModel";

const good = (status) => ["live", "partial", "empty"].includes(status);
export const MetaSectionStatus = ({ status, tt, onRetry }) =>
  !["live", "empty"].includes(status) ? (
    <p className="an-note is-warning" role="status">
      {tt(`meta_state_${status || "unavailable"}`)}
      {onRetry ? (
        <Btn size="sm" icon="refresh" onClick={onRetry}>
          {tt("retry")}
        </Btn>
      ) : null}
    </p>
  ) : null;
export function MetaDateControls({ filters, range, onChange, tt }) {
  const [draft, setDraft] = useState({ dateFrom: filters.dateFrom || "", dateTo: filters.dateTo || "" });
  useEffect(() => {
    if (range) setDraft({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  }, [range]);
  return (
    <form
      className="an-range"
      onSubmit={(e) => {
        e.preventDefault();
        if (validMetaDates(draft.dateFrom, draft.dateTo)) onChange({ preset: "custom", ...draft });
      }}
    >
      <label className="an-range__field">
        <span>{tt("range")}</span>
        <Select
          value={filters.preset}
          onChange={(preset) => {
            if (preset !== "custom") onChange({ preset });
          }}
        >
          {META_PRESETS.map((p) => (
            <option key={p} value={p}>
              {tt(`meta_preset_${p}`)}
            </option>
          ))}
        </Select>
      </label>
      <label className="an-range__field">
        <span>{tt("dateFrom")}</span>
        <TextInput
          type="date"
          value={draft.dateFrom}
          max={draft.dateTo}
          onChange={(dateFrom) => setDraft((d) => ({ ...d, dateFrom }))}
        />
      </label>
      <label className="an-range__field">
        <span>{tt("dateTo")}</span>
        <TextInput
          type="date"
          value={draft.dateTo}
          min={draft.dateFrom}
          onChange={(dateTo) => setDraft((d) => ({ ...d, dateTo }))}
        />
      </label>
      <Btn type="submit" disabled={!validMetaDates(draft.dateFrom, draft.dateTo)}>
        {tt("apply")}
      </Btn>
      <p className="an-range__summary">{tt("meta_dateNote")}</p>
    </form>
  );
}
export const MetaKpis = ({ data, tt, lang }) => (
  <div className="an-kpis an-traffic-kpis">
    {META_KPIS.map((key) => (
      <KpiCard
        key={key}
        label={tt(`meta_${key}`)}
        help={tt(`meta_help_${key}`)}
        compared={data.kpis[key] || { value: null, previous: null, change: null }}
        display={metaFormat(data.kpis[key]?.value, key, data.account.currency, lang)}
        tt={tt}
        lang={lang}
      />
    ))}
  </div>
);

export function MetaTrend({ data, tt, lang }) {
  const [metric, setMetric] = useState("spend");
  const palette = useChartPalette();
  const format = (v) => metaFormat(v, metric, data.account.currency, lang);
  return (
    <Panel
      title={tt("meta_trend")}
      hint={tt("meta_trendNote")}
      actions={
        <label className="an-range__field">
          <span className="pc-sr-only">{tt("meta_metric")}</span>
          <Select value={metric} onChange={setMetric}>
            {["spend", "impressions", "linkClicks", "purchases", "purchaseValue", "purchaseRoas"].map((key) => (
              <option key={key} value={key}>
                {tt(`meta_${key}`)}
              </option>
            ))}
          </Select>
        </label>
      }
    >
      <MetaSectionStatus status={data.sections.trend} tt={tt} />
      <ChartFrame
        label={tt(`meta_${metric}`)}
        tt={tt}
        table={
          <DataTable
            rows={data.trend}
            emptyText={tt("meta_noRows")}
            columns={[
              { key: "date", label: tt("col_date"), render: (r) => r.bucket },
              { key: "metric", label: tt(`meta_${metric}`), render: (r) => format(r[metric]) },
            ]}
          />
        }
      >
        <Line
          data={{
            labels: data.trend.map((r) => bucketLabel(r.bucket, "day", lang)),
            datasets: [
              {
                label: tt(`meta_${metric}`),
                data: data.trend.map((r) => r[metric]),
                borderColor: palette.series1,
                backgroundColor: palette.series1,
                borderWidth: 2,
                pointRadius: 1,
                pointHoverRadius: 4,
                tension: 0,
                spanGaps: false,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 200 },
            interaction: { mode: "index", intersect: false },
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => `${tt(`meta_${metric}`)}: ${format(ctx.parsed.y)}` } },
            },
            scales: {
              x: { grid: { display: false }, ticks: { color: palette.ink, maxTicksLimit: 8, maxRotation: 0 } },
              y: {
                beginAtZero: true,
                grid: { color: palette.grid },
                ticks: { color: palette.ink, callback: (value) => format(value) },
              },
            },
          }}
        />
      </ChartFrame>
    </Panel>
  );
}
const resultLabel = (type, tt) => {
  const key = `meta_action_${type.replace(/\./g, "_")}`;
  return metaCopy[key] ? tt(key) : type;
};
export function MetaHierarchy({ filters, currency, tt, lang }) {
  const [campaign, setCampaign] = useState(null),
    [adset, setAdset] = useState(null),
    [after, setAfter] = useState(null),
    [history, setHistory] = useState([]);
  const [draft, setDraft] = useState(""),
    [search, setSearch] = useState(""),
    [status, setStatus] = useState(""),
    [objective, setObjective] = useState(""),
    [sort, setSort] = useState("spend"),
    [direction, setDirection] = useState("desc");
  const level = adset ? "ad" : campaign ? "adset" : "campaign";
  const result = useFetchMetaHierarchyQuery(
    {
      ...filters,
      level,
      parentId: adset?.id || campaign?.id,
      after: after || undefined,
      search: search || undefined,
      status: status || undefined,
      objective: objective || undefined,
      sort,
      direction,
    },
    { refetchOnMountOrArgChange: true },
  );
  const query = { ...result, data: result.currentData };
  const data = query.data;
  const resetPage = () => {
    setAfter(null);
    setHistory([]);
  };
  useEffect(() => {
    setAfter(null);
    setHistory([]);
    setCampaign(null);
    setAdset(null);
  }, [filters.preset, filters.dateFrom, filters.dateTo]);
  const numberColumn = (key) => ({
    key,
    label: tt(`meta_${key}`),
    sortKey: ["spend", "impressions", "linkClicks"].includes(key) ? key : undefined,
    align: "end",
    render: (r) => metaFormat(r[key], key, currency, lang),
  });
  return (
    <Panel title={tt(`meta_level_${level}`)} hint={tt("meta_hierarchyNote")}>
      <nav className="an-filters" aria-label={tt("meta_hierarchy")}>
        <Btn
          size="sm"
          disabled={!campaign}
          onClick={() => {
            setCampaign(null);
            setAdset(null);
            resetPage();
          }}
        >
          {tt("meta_level_campaign")}
        </Btn>
        {campaign ? (
          <Btn
            size="sm"
            disabled={!adset}
            onClick={() => {
              setAdset(null);
              resetPage();
            }}
          >
            {campaign.name}
          </Btn>
        ) : null}
        {adset ? <span>{adset.name}</span> : null}
      </nav>
      <form
        className="an-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(draft);
          resetPage();
        }}
      >
        <label>
          <span className="pc-sr-only">{tt("meta_search")}</span>
          <TextInput value={draft} maxLength={80} placeholder={tt("meta_search")} onChange={setDraft} />
        </label>
        <Btn size="sm" type="submit">
          {tt("apply")}
        </Btn>
        <label>
          <span className="pc-sr-only">{tt("meta_status")}</span>
          <Select
            value={status}
            onChange={(value) => {
              setStatus(value);
              resetPage();
            }}
          >
            <option value="">{tt("meta_allStatuses")}</option>
            {["ACTIVE", "PAUSED", "ARCHIVED", "DELETED"].map((v) => (
              <option key={v} value={v}>
                {tt(`meta_status_${v}`)}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="pc-sr-only">{tt("meta_objective")}</span>
          <Select
            value={objective}
            onChange={(value) => {
              setObjective(value);
              resetPage();
            }}
          >
            <option value="">{tt("meta_allObjectives")}</option>
            {[
              "OUTCOME_SALES",
              "OUTCOME_TRAFFIC",
              "OUTCOME_ENGAGEMENT",
              "OUTCOME_LEADS",
              "OUTCOME_AWARENESS",
              "OUTCOME_APP_PROMOTION",
              "CONVERSIONS",
              "LINK_CLICKS",
              "MESSAGES",
            ].map((v) => (
              <option key={v} value={v}>
                {tt(`meta_objective_${v}`)}
              </option>
            ))}
          </Select>
        </label>
      </form>
      <DataState query={query} tt={tt}>
        <MetaSectionStatus status={data?.status} tt={tt} onRetry={() => query.refetch()} />
        {data && good(data.status) ? (
          <>
            <DataTable
              rows={data.rows}
              caption={tt(`meta_level_${level}`)}
              rowKey={(r) => r.id}
              sort={sort}
              direction={direction}
              onSort={(key) => {
                setDirection(sort === key && direction === "desc" ? "asc" : "desc");
                setSort(key);
                resetPage();
              }}
              emptyText={tt("meta_noRows")}
              columns={[
                {
                  key: "name",
                  className: "an-meta-name",
                  label: tt("meta_name"),
                  render: (r) => (
                    <>
                      {level === "ad" ? (
                        <strong>{r.name}</strong>
                      ) : (
                        <button
                          className="pc-link"
                          type="button"
                          onClick={() => {
                            if (level === "campaign") setCampaign({ id: r.id, name: r.name });
                            else setAdset({ id: r.id, name: r.name });
                            setSearch("");
                            setDraft("");
                            setStatus("");
                            setObjective("");
                            resetPage();
                          }}
                        >
                          {r.name}
                        </button>
                      )}
                      <div className="an-muted">{r.id}</div>
                    </>
                  ),
                },
                {
                  key: "objective",
                  label: tt("meta_objective"),
                  render: (r) =>
                    r.objective
                      ? !metaCopy[`meta_objective_${r.objective}`]
                        ? r.objective
                        : tt(`meta_objective_${r.objective}`)
                      : "—",
                },
                {
                  key: "status",
                  label: tt("meta_status"),
                  render: (r) =>
                    r.status
                      ? metaCopy[`meta_status_${r.status}`]
                        ? tt(`meta_status_${r.status}`)
                        : r.status
                      : tt("unavailable"),
                },
                numberColumn("spend"),
                numberColumn("impressions"),
                numberColumn("linkClicks"),
                numberColumn("linkCtr"),
                numberColumn("linkCpc"),
                {
                  key: "results",
                  label: tt("meta_results"),
                  render: (r) =>
                    r.results.length
                      ? r.results.map((a) => (
                          <div key={a.type}>
                            {resultLabel(a.type, tt)}: {metaFormat(a.value, "results", currency, lang)}
                            {a.cost !== null ? (
                              <small>
                                {" "}
                                · {metaFormat(a.cost, "cost", currency, lang)} / {tt("meta_result")}
                              </small>
                            ) : null}
                          </div>
                        ))
                      : tt("unavailable"),
                },
                numberColumn("purchaseRoas"),
                {
                  key: "attribution",
                  label: tt("meta_attribution"),
                  render: (r) =>
                    typeof r.attribution === "string"
                      ? r.attribution
                      : Array.isArray(r.attribution)
                        ? r.attribution.map((a) => `${a.event_type}: ${a.window_days ?? "—"}d`).join(", ")
                        : tt("meta_inheritedAttribution"),
                },
              ]}
            />
            <div className="an-pager">
              <Btn
                size="sm"
                disabled={!history.length}
                onClick={() => {
                  setAfter(history[history.length - 1]);
                  setHistory((h) => h.slice(0, -1));
                }}
              >
                {tt("previous")}
              </Btn>
              <span>{fill(tt("meta_page"), { page: history.length + 1 })}</span>
              <Btn
                size="sm"
                disabled={!data.next}
                onClick={() => {
                  setHistory((h) => [...h, after]);
                  setAfter(data.next);
                }}
              >
                {tt("next")}
              </Btn>
            </div>
          </>
        ) : null}
      </DataState>
    </Panel>
  );
}
export function MetaPlacements({ data, tt, lang }) {
  const [detail, setDetail] = useState(false);
  const section = detail ? "placements" : "platforms";
  return (
    <Panel
      title={tt("meta_platforms")}
      hint={tt("meta_platformNote")}
      actions={
        <Btn size="sm" onClick={() => setDetail((v) => !v)}>
          {tt(detail ? "meta_platformSummary" : "meta_placementDetail")}
        </Btn>
      }
    >
      <MetaSectionStatus status={data.sections[section]} tt={tt} />
      <DataTable
        rows={data[section]}
        emptyText={tt("meta_noRows")}
        columns={[
          {
            key: "platform",
            label: tt("meta_platform"),
            render: (r) => (
              <>
                <strong>{r.platform}</strong>
                {detail ? <div className="an-muted">{r.position}</div> : null}
              </>
            ),
          },
          ...["spend", "impressions", "linkClicks", "linkCtr", "linkCpc"].map((key) => ({
            key,
            label: tt(`meta_${key}`),
            align: "end",
            render: (r) => metaFormat(r[key], key, data.account.currency, lang),
          })),
        ]}
      />
    </Panel>
  );
}
function MetaComparison({ data, filters, tt, lang }) {
  const result = useFetchMetaComparisonQuery(filters, { refetchOnMountOrArgChange: true });
  const query = { ...result, data: result.currentData };
  const comparison = query.data;
  const measure = (label, value, key, currency) => (
    <div key={label}>
      <dt>{label}</dt>
      <dd>{metaFormat(value, key, currency, lang)}</dd>
    </div>
  );
  return (
    <Panel title={tt("meta_comparison")} hint={tt("meta_comparisonNote")}>
      <DataState query={query} tt={tt}>
        {comparison?.ga4 ? (
          <>
            <p className="an-muted">
              {tt("meta_timezones")}: Meta {comparison.metaTimezone} · GA4 {comparison.ga4.timezone || "—"} ·{" "}
              {tt("meta_ledger")} {comparison.ledger.timezone || "—"}
            </p>
            <div className="an-grid an-grid--3">
              <div>
                <h4>Meta Ads · {data.account.currency}</h4>
                <dl>
                  {["spend", "purchases", "purchaseValue", "purchaseRoas"].map((key) =>
                    measure(tt(`meta_${key}`), data.kpis[key]?.value, key, data.account.currency),
                  )}
                </dl>
              </div>
              <div>
                <h4>GA4 · EUR</h4>
                <MetaSectionStatus status={comparison.ga4.status} tt={tt} />
                <dl>
                  {measure(tt("meta_paidSocialSessions"), comparison.ga4.paidSocialSessions, "count", "EUR")}
                  {measure(tt("meta_gaPurchases"), comparison.ga4.purchaseEvents, "count", "EUR")}
                  {measure(tt("meta_gaValue"), comparison.ga4.trackedOrderValue, "purchaseValue", "EUR")}
                </dl>
              </div>
              <div>
                <h4>{tt("meta_ledger")} · EUR</h4>
                <MetaSectionStatus status={comparison.ledger.status} tt={tt} />
                <dl>
                  {measure(tt("meta_websiteNetSales"), comparison.ledger.websiteNetSales, "purchaseValue", "EUR")}
                  {measure(tt("meta_websiteSalesCount"), comparison.ledger.websiteSalesCount, "count", "EUR")}
                  {measure(tt("meta_allNetSales"), comparison.ledger.allChannelNetSales, "purchaseValue", "EUR")}
                </dl>
              </div>
            </div>
          </>
        ) : (
          <MetaSectionStatus status={comparison?.status} tt={tt} />
        )}
      </DataState>
    </Panel>
  );
}
export function MetaDashboard({ data, filters, tt, lang }) {
  const secondary = ["reach", "frequency", "linkCpc", "cpm", "landingPageViews", "costPerPurchase", "purchaseValue"];
  return (
    <>
      <p className="an-muted" role="status">
        {data.account.name} · {data.account.currency} · {data.account.timezone} · {data.range.dateFrom} —{" "}
        {data.range.dateTo} ·{" "}
        {fill(tt("meta_fetched"), {
          at: new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "sq-AL", {
            timeZone: data.account.timezone,
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(data.fetchedAt)),
        })}
      </p>
      <p className="an-note is-warning">{tt("meta_attributionNote")}</p>
      {data.range.includesToday ? <p className="an-note is-warning">{tt("meta_todayNote")}</p> : null}
      <MetaSectionStatus status={data.status} tt={tt} />
      {data.status === "empty" ? <Notice title={tt("meta_empty")}>{tt("meta_noRows")}</Notice> : null}
      <MetaKpis data={data} tt={tt} lang={lang} />
      <MetaTrend data={data} tt={tt} lang={lang} />
      <MetaHierarchy filters={filters} currency={data.account.currency} tt={tt} lang={lang} />
      <MetaPlacements data={data} tt={tt} lang={lang} />
      <div className="an-grid an-grid--2">
        <Panel title={tt("meta_delivery")}>
          <dl className="an-meta-measures">
            {secondary.map((key) => (
              <div key={key}>
                <dt>{tt(`meta_${key}`)}</dt>
                <dd>{metaFormat(data.kpis[key]?.value, key, data.account.currency, lang)}</dd>
              </div>
            ))}
          </dl>
        </Panel>
        <Panel title={tt("meta_insights")} hint={tt("meta_insightsNote")}>
          {data.insights.length ? (
            data.insights.map((i) => (
              <p key={i.code}>
                {fill(tt(`meta_insight_${i.code}`), {
                  value: metaFormat(
                    i.value,
                    i.code === "instagram_spend" ? "spend" : "linkCtr",
                    data.account.currency,
                    lang,
                  ),
                })}
              </p>
            ))
          ) : (
            <p className="an-muted">{tt("meta_noInsights")}</p>
          )}
        </Panel>
      </div>
      <MetaComparison data={data} filters={filters} tt={tt} lang={lang} />
    </>
  );
}
export default function MetaAdsSection({ tt, lang }) {
  const [params, setParams] = useSearchParams();
  const filters = readMetaFilters(params);
  const result = useFetchMetaDashboardQuery(filters, { refetchOnMountOrArgChange: true });
  const query = { ...result, data: result.currentData };
  const data = query.data;
  // Freeze the resolved account-local dates for independent reports, including across midnight.
  const dates = data?.range ? { preset: "custom", dateFrom: data.range.dateFrom, dateTo: data.range.dateTo } : filters;
  return (
    <>
      <MetaDateControls
        filters={filters}
        range={data?.range}
        tt={tt}
        onChange={(next) => {
          const p = new URLSearchParams(params);
          p.set("metaPreset", next.preset);
          for (const [key, param] of [
            ["dateFrom", "metaFrom"],
            ["dateTo", "metaTo"],
          ]) {
            if (next[key]) p.set(param, next[key]);
            else p.delete(param);
          }
          setParams(p);
        }}
      />
      <div className="an-panel__actions">
        <Btn size="sm" icon="refresh" disabled={query.isFetching} onClick={() => query.refetch()}>
          {tt("refreshData")}
        </Btn>
      </div>
      <DataState query={query} tt={tt}>
        {data && good(data.status) ? (
          <MetaDashboard data={data} filters={dates} tt={tt} lang={lang} />
        ) : data ? (
          <Notice tone="warning" title={tt(`meta_state_${data.status}`)}>
            {tt("meta_connectHelp")}
          </Notice>
        ) : null}
      </DataState>
    </>
  );
}
